import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useCaptured } from '../capture/CaptureProvider.js';
import { actNudge, completeObligation, completeTask, getToday, KEEPALIVE } from '../lifeos/api.js';
import FocusPicker from '../lifeos/FocusPicker.js';
import { clock, daysLeftLabel, humanize, minutesLabel, money, plural, prettyHandle, sleepLabel, taskCode } from '../lifeos/format.js';
import { sphereColor, sphereName, useSpheres } from '../lifeos/spheres.js';
import TaskEditSheet from '../lifeos/TaskEditSheet.js';
import { TaskActionsSheet, TaskItem, useTaskActions } from '../lifeos/tasks.js';
import { useToast } from '../lifeos/toast.js';
import { DueChip, Loader } from '../lifeos/ui.js';
import { useLoad } from '../lifeos/useLoad.js';
import type { Nudge, Sphere, TaskRow, TodayPayload, UpcomingItem } from '../lifeos/types.js';
import { Card, EmptyState, Group, Icon, IconTile, Metric, Page, Ring, Row, type IconName } from '../ui/index.js';
import { upcomingHref } from '../lifeos/links.js';
import Lights from '../components/Lights.js';

const WEEKDAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** '2026-10-09' → 'Friday, October 9'. */
export function longDay(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dow = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)).getUTCDay();
  return `${WEEKDAY[dow]}, ${MONTH[(m ?? 1) - 1]} ${d}`;
}

/**
 * Today (`/`, spec 2026-10-09 §6): "what do I do now, and is anything on
 * fire?" Top to bottom: the one Now card, Top 3, then only the stack rows
 * that have something to say (replies waiting, to sort, approvals,
 * unblocked, nudges), what's coming up today (agenda + bills due this week:
 * the money signal), and the body readouts. Capture lives in the tab bar.
 * Everything is `GET /api/today`; nothing here composes or ranks on its own.
 */
export default function Home(): JSX.Element {
  const today = useLoad(getToday, []);
  const spheres = useSpheres();
  const actions = useTaskActions(today.reload);
  const [menu, setMenu] = useState<TaskRow | null>(null);
  const [editing, setEditing] = useState<TaskRow | null>(null);
  const [picking, setPicking] = useState(false);
  useCaptured(() => void today.reload());

  const d = today.data;
  return (
    <Page
      title="Today"
      sub={d ? longDay(d.date) : undefined}
      wide
      actions={
        <>
          {/* Timeline left the sidebar (2026-11-02); it lives behind this button. */}
          <Link to="/timeline" className="ui-iconbtn" aria-label="Timeline" title="Timeline">
            <Icon name="history" size={22} />
          </Link>
          {d && <BodyGlyph health={d.health} />}
        </>
      }
      className="home"
    >
      <Loader state={today}>
        {(data) => (
          <HomeBody
            d={data}
            spheres={spheres}
            hidden={actions.hidden}
            onComplete={actions.complete}
            onSnooze={(t) => actions.snooze(t, 'tomorrow')}
            onMenu={setMenu}
            onPick={() => setPicking(true)}
            reload={today.reload}
          />
        )}
      </Loader>
      {menu && (
        <TaskActionsSheet task={menu} actions={actions} onEdit={() => setEditing(menu)} onClose={() => setMenu(null)} />
      )}
      {editing && <TaskEditSheet task={editing} onClose={() => setEditing(null)} onSaved={() => void today.reload()} />}
      {picking && d && (
        <FocusPicker today={d.date} current={d.focus} onClose={() => setPicking(false)} onSaved={() => void today.reload()} />
      )}
    </Page>
  );
}

function HomeBody({
  d,
  spheres,
  hidden,
  onComplete,
  onSnooze,
  onMenu,
  onPick,
  reload,
}: {
  d: TodayPayload;
  spheres: Sphere[];
  hidden: ReadonlySet<string>;
  onComplete: (t: { id: string; shortCode: number; title: string }) => void;
  onSnooze: (t: TaskRow) => void;
  onMenu: (t: TaskRow) => void;
  onPick: () => void;
  reload: () => Promise<void>;
}): JSX.Element {
  const focus = d.focus.filter((t) => !hidden.has(t.id));
  return (
    <div className="home__grid">
      <div className="home__col">
        <NowCard d={d} spheres={spheres} hidden={hidden} onComplete={onComplete} reload={reload} />
        {focus.length > 0 ? (
          <Group header="Top 3" id="home-focus" action={{ label: 'Edit', onClick: onPick }} className="home__focus">
            {focus.map((t) => (
              <TaskItem
                key={t.id}
                task={t}
                today={d.date}
                color={sphereColor(spheres, t.sphereKey)}
                onToggle={() => onComplete(t)}
                onSnooze={() => onSnooze(t)}
                onMenu={() => onMenu(t)}
              />
            ))}
          </Group>
        ) : (
          <EmptyState
            icon="tasks"
            title="Nothing else actionable right now."
            hint="Capture anything on your mind, or pick today's 3."
            action={
              <button type="button" className="ui-btn ui-btn--secondary ui-btn--md" onClick={onPick}>
                Pick today's 3
              </button>
            }
          />
        )}
      </div>
      {/* Two columns up to 1800px; on wider screens the right column splits in two (CSS display: contents). */}
      <div className="home__col home__col--rest">
        <div className="home__col">
          <NeedsYou d={d} reload={reload} />
          <Lights />
        </div>
        <div className="home__col">
          <ComingUp d={d} spheres={spheres} />
          <Body health={d.health} />
        </div>
      </div>
    </div>
  );
}

// --- Now ----------------------------------------------------------------------------------------

function NowCard({
  d,
  spheres,
  hidden,
  onComplete,
  reload,
}: {
  d: TodayPayload;
  spheres: Sphere[];
  hidden: ReadonlySet<string>;
  onComplete: (t: { id: string; shortCode: number; title: string }) => void;
  reload: () => Promise<void>;
}): JSX.Element {
  const toast = useToast();
  const [obligationDone, setObligationDone] = useState<string | null>(null);
  const { now } = d;
  const task = now.kind === 'task' ? d.focus.find((t) => t.id === now.ref) : undefined;
  const done =
    (now.kind === 'task' && now.ref !== null && hidden.has(now.ref)) ||
    (now.kind === 'obligation' && now.ref !== null && obligationDone === now.ref);
  const accent = task ? sphereColor(spheres, task.sphereKey) : now.kind === 'obligation' ? 'var(--warn)' : 'var(--accent)';

  const meta: string[] = [];
  if (now.shortCode !== undefined) meta.push(taskCode(now.shortCode));
  const est = minutesLabel(task?.estimateMin ?? null);
  if (est) meta.push(est);

  function completeNow(): void {
    if (!now.ref) return;
    if (now.kind === 'task') {
      onComplete({ id: now.ref, shortCode: now.shortCode ?? 0, title: now.title });
      return;
    }
    const ref = now.ref;
    // The due date on screen pins the period, so a retry never pays the next one;
    // a 409 "due date has moved" (paid elsewhere meanwhile) reloads.
    const dueOn = d.dueSoon.find((u) => u.id === ref && u.type !== 'event' && u.type !== 'task' && u.type !== 'birthday')?.dueOn;
    setObligationDone(ref);
    toast.defer({
      text: `Done: ${now.title}`,
      commit: () => completeObligation(ref, dueOn ? { dueOn } : {}, KEEPALIVE),
      onUndo: () => setObligationDone(null),
      onDone: () => void reload(),
      onError: () => {
        setObligationDone(null);
        void reload();
      },
      failText: "Couldn't mark that done",
    });
  }

  let action: JSX.Element | null = null;
  if (!done && now.ref && (now.kind === 'task' || now.kind === 'obligation')) {
    action = (
      <button type="button" className="ui-btn ui-btn--primary ui-btn--lg lo-now__act" onClick={completeNow}>
        <Icon name="check" size={20} />
        {now.kind === 'task' ? 'Done' : 'Mark done'}
      </button>
    );
  } else if (now.kind === 'message') {
    action = (
      <Link className="ui-btn ui-btn--primary ui-btn--lg lo-now__act" to="/inbox?status=needs_reply">
        <Icon name="inbox" size={20} />
        Open inbox
      </Link>
    );
  } else if (now.kind === 'event') {
    action = (
      <Link className="ui-btn ui-btn--secondary ui-btn--lg lo-now__act" to="/calendar">
        <Icon name="calendar" size={20} />
        Calendar
      </Link>
    );
  }

  return (
    <Card
      tone="now"
      accent={accent}
      className={`lo-now lo-now--${now.kind}${done ? ' lo-now--done' : ''}`}
      label="Now"
      eyebrow={
        <>
          <span className="lo-now__pulse" aria-hidden="true" />
          Now
          {task && <DueChip dueOn={task.dueOn} today={d.date} />}
        </>
      }
    >
      <h2 className="lo-now__title" id="home-now">
        {now.title}
      </h2>
      {(now.why || meta.length > 0) && (
        <p className="lo-now__why">
          {meta.length > 0 && <span className="lo-code">{meta.join(' · ')}</span>}
          {now.why}
        </p>
      )}
      {action}
    </Card>
  );
}

// --- Needs you: only rows with something to say -------------------------------------------------

/** What a nudge row needs to know from its free-form `meta` (written by `deliver()`). */
export function nudgeMeta(n: Pick<Nudge, 'meta'>): { kind: string | null; refId: string | null; taskId: string | null; risk: string | null } {
  const m = n.meta && typeof n.meta === 'object' ? (n.meta as Record<string, unknown>) : {};
  const ref = m.ref && typeof m.ref === 'object' ? (m.ref as Record<string, unknown>) : {};
  const s = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
  return {
    kind: s(m.kind),
    refId: s(ref.id),
    taskId: s(m.taskId) ?? (ref.table === 'tasks' ? s(ref.id) : null),
    risk: s(m.risk),
  };
}

/** A body that only names the nudge's kind ("event") says nothing; a Signal reply grammar line is not for the web. */
export function nudgeSub(n: Pick<Nudge, 'body' | 'meta'> & { title?: string }): string | null {
  let body = n.body?.trim() ?? '';
  // "Phone bill renews" / "Phone bill renews due Fri": the title already said the first half.
  const title = n.title?.trim() ?? '';
  if (title && body.toLowerCase().startsWith(title.toLowerCase()) && body.length > title.length) {
    const rest = body.slice(title.length).replace(/^[\s:·—–-]+/, '');
    if (rest) body = rest.charAt(0).toUpperCase() + rest.slice(1);
  }
  const { kind, risk } = nudgeMeta(n);
  const signalGrammar = (kind === 'approval' || kind === 'reminder') && /\bReply\b/.test(body);
  if (kind === 'approval' && (body === '' || signalGrammar)) return risk ? `${humanize(risk)} risk` : null;
  if (body === '' || signalGrammar || /^(event|appointment|birthday|deadline)$/i.test(body)) return null;
  return body;
}

/** A nudge body clamped to two lines with a More toggle (same pattern as InsightCards' InsightBody). */
export function NudgeBody({ body }: { body: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  const long = body.length > 110 || body.includes('\n');
  if (!long) return <>{body}</>;
  return (
    <>
      <span className={`nudge-body${open ? '' : ' nudge-body--clamp'}`}>{body}</span>
      <button type="button" className="nudge-more" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {open ? 'Less' : 'More'}
      </button>
    </>
  );
}

/**
 * Replies waiting, To sort, approvals, unblocked threads and up to three
 * nudges (minus any that only repeat an agenda row). An approval nudge links
 * to its card; a reminder's Done completes the task too (closing the nudge
 * alone would leave the task open); anything else is acknowledged.
 */
function NeedsYou({ d, reload }: { d: TodayPayload; reload: () => Promise<void> }): JSX.Element | null {
  const toast = useToast();
  const [gone, setGone] = useState<ReadonlySet<string>>(new Set());
  // A nudge that only repeats a row already on screen (agenda, due this week) is noise.
  const agenda = [...d.agenda.map((a) => a.title), ...d.dueSoon.map((u) => u.title)]
    .map((t) => t.trim().toLowerCase())
    .filter((t) => t !== '');
  const nudges = d.nudges
    .filter((n) => !gone.has(n.id))
    .filter((n) => !agenda.some((t) => n.title.trim().toLowerCase().startsWith(t)))
    .slice(0, 3);

  const restore = (id: string) => (): void =>
    setGone((s) => {
      const n = new Set(s);
      n.delete(id);
      return n;
    });

  function act(n: Nudge, taskId: string | null): void {
    setGone((s) => new Set(s).add(n.id));
    toast.defer({
      text: `Done: ${n.title}`,
      commit: async () => {
        await Promise.all([taskId ? completeTask(taskId, KEEPALIVE) : null, actNudge(n.id, KEEPALIVE)]);
      },
      onUndo: restore(n.id),
      onDone: () => void reload(),
      onError: restore(n.id),
    });
  }

  const who = d.waitingOnYou.top.map((w) => prettyHandle(w.from)).filter(Boolean);
  const rows: ReactNode[] = [];
  if (d.waitingOnYou.count > 0) {
    rows.push(
      <Row
        key="waiting"
        className="home__need"
        leading={<IconTile name="chat" color="#0a84ff" />}
        title={plural(d.waitingOnYou.count, 'reply', 'replies') + ' waiting'}
        sub={who.length > 0 ? who.join(', ') : undefined}
        to="/inbox?status=needs_reply"
      />,
    );
  }
  if (d.toSortCount > 0) {
    rows.push(
      <Row
        key="sort"
        className="home__need"
        leading={<IconTile name="sort" color="#ff9f0a" />}
        title={`${d.toSortCount} to sort`}
        sub="Captured without a date or project"
        to="/tasks?view=to_sort"
      />,
    );
  }
  if (d.approvalsPending > 0) {
    rows.push(
      <Row
        key="approvals"
        className="home__need"
        leading={<IconTile name="bot" color="#5856d6" />}
        title={plural(d.approvalsPending, 'approval')}
        sub="Agents are waiting on a decision"
        to="/agents"
      />,
    );
  }
  for (const u of d.unblocked) {
    rows.push(
      <Row
        key={`u${u.messageId}`}
        className="home__need"
        leading={<IconTile name="bolt" color="#34c759" />}
        title={
          <>
            <span className="lo-code">{taskCode(u.taskCode)}</span> {u.title}
          </>
        }
        sub={
          <>
            {u.from} replied: <span className="lo-quote">{u.snippet}</span>
          </>
        }
        to="/inbox"
      />,
    );
  }
  for (const n of nudges) {
    const meta = nudgeMeta(n);
    const sub = nudgeSub(n);
    let action: JSX.Element;
    if (meta.kind === 'approval' && meta.refId) {
      action = (
        <Link className="ui-btn ui-btn--secondary ui-btn--sm" to={`/agents?approval=${encodeURIComponent(meta.refId)}`}>
          Review
        </Link>
      );
    } else {
      const taskId = meta.kind === 'reminder' ? meta.taskId : null;
      const label = meta.kind === 'task' || meta.kind === 'reminder' ? 'Done' : 'Got it';
      action = (
        <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={() => act(n, taskId)}>
          {label}
        </button>
      );
    }
    rows.push(
      <Row
        key={n.id}
        className="home__nudge"
        leading={<IconTile name="bell" color="#ff453a" />}
        title={n.title}
        sub={sub ? <NudgeBody body={sub} /> : undefined}
        trailing={action}
      />,
    );
  }
  if (rows.length === 0) return null;
  return (
    <Group header="Needs you" id="home-needs">
      {rows}
    </Group>
  );
}

// --- Coming up: today's agenda + this week's bills ----------------------------------------------

function ComingUp({ d, spheres }: { d: TodayPayload; spheres: Sphere[] }): JSX.Element | null {
  const rows: ReactNode[] = d.agenda.map((e) => (
    <Row
      key={`${e.id}:${e.startsAt}`}
      className="home__event"
      leading={<span className="home__time num">{e.allDay ? 'All day' : clock(e.startsAt)}</span>}
      title={e.title}
      sub={[e.location, e.people.join(', ')].filter(Boolean).join(' · ') || undefined}
      to="/calendar"
      chevron={false}
    />
  ));
  // The Now card already shows its obligation, and today's agenda already shows
  // today's events; don't list either twice.
  const onAgenda = new Set(d.agenda.map((e) => e.title.trim().toLowerCase()));
  const dupe = (x: (typeof d.dueSoon)[number]): boolean =>
    (d.now.kind === 'obligation' && x.id === d.now.ref) || (x.type === 'event' && x.daysLeft === 0 && onAgenda.has(x.title.trim().toLowerCase()));
  for (const u of d.dueSoon.filter((x) => !dupe(x))) rows.push(<ComingUpRow key={`${u.type}:${u.id}:${u.dueOn}`} u={u} spheres={spheres} />);
  const more = (d.dueSoonCount ?? d.dueSoon.length) - d.dueSoon.length;
  if (rows.length === 0) return null;
  return (
    <Group
      header="Coming up"
      id="home-coming"
      action={more > 0 ? { label: `${more} more this week`, to: '/library?days=7' } : undefined}
    >
      {rows}
    </Group>
  );
}

/** Every upcoming type gets its own icon and words (2026-10-13: not just birthdays). */
const COMING_ICON: Record<UpcomingItem['type'], IconName> = {
  event: 'calendar',
  task: 'tasks',
  birthday: 'star',
  obligation: 'receipt',
  document: 'file',
  service: 'repeat',
  warranty: 'shield',
};
const COMING_WORD: Record<UpcomingItem['type'], string> = {
  event: 'Event',
  task: 'Task due',
  birthday: 'Birthday',
  obligation: 'Due',
  document: 'Expires',
  service: 'Renews',
  warranty: 'Warranty ends',
};

/**
 * One Coming up row. An event leads with its time in its job's colour; the
 * rest lead with a type icon. The sub line says what it is and for which job
 * (Personal is left unsaid), plus the amount for a bill.
 */
export function ComingUpRow({ u, spheres }: { u: UpcomingItem; spheres: Sphere[] }): JSX.Element {
  const color = sphereColor(spheres, u.sphere);
  const job = u.sphere && u.sphere !== 'personal' ? sphereName(spheres, u.sphere) : null;
  const isEvent = u.type === 'event' && u.startsAt;
  const word = u.type === 'obligation' && u.amountCents !== undefined ? 'Bill' : (COMING_WORD[u.type] ?? humanize(u.type));
  const sub = [word, u.amountCents !== undefined ? money(u.amountCents) : null, job].filter(Boolean).join(' · ');
  return (
    <Row
      className={`home__due home__due--${u.type}`}
      leading={
        isEvent ? (
          <span className="home__time num" style={{ color }}>
            {u.allDay ? 'All day' : clock(u.startsAt!)}
          </span>
        ) : (
          <IconTile name={COMING_ICON[u.type] ?? 'circle'} color={color} />
        )
      }
      title={u.title}
      sub={sub}
      detail={<span className={u.daysLeft < 0 ? 'home__late' : undefined}>{daysLeftLabel(u.daysLeft)}</span>}
      to={upcomingHref(u)}
    />
  );
}

// --- Body ---------------------------------------------------------------------------------------

function Body({ health }: { health: TodayPayload['health'] }): JSX.Element | null {
  const items: JSX.Element[] = [];
  if (health.sleepMinutes !== undefined) items.push(<Metric key="sleep" label="Sleep" value={sleepLabel(health.sleepMinutes)} />);
  if (health.sleepScore !== undefined) {
    const v = Math.round(health.sleepScore);
    items.push(<Metric key="score" label="Sleep score" value={v} ring={{ value: v, max: 100, color: 'var(--ring-sleep)' }} />);
  }
  if (health.readiness !== undefined) {
    const v = Math.round(health.readiness);
    items.push(<Metric key="ready" label="Readiness" value={v} ring={{ value: v, max: 100, color: 'var(--accent)' }} />);
  }
  if (health.restingHr !== undefined) items.push(<Metric key="rhr" label="Resting HR" value={Math.round(health.restingHr)} unit="bpm" />);
  if (health.steps !== undefined) items.push(<Metric key="steps" label="Steps" value={Math.round(health.steps).toLocaleString('en-US')} />);
  if (items.length === 0) return null;
  return (
    <section className="ui-groupwrap" aria-labelledby="home-body-head">
      <div className="ui-group__head">
        <h2 className="ui-group__title" id="home-body-head">
          Body
        </h2>
        <Link className="ui-group__action" to="/health">
          Health
        </Link>
      </div>
      <Link to="/health" className="home__body" aria-label="Health today">
        {items}
      </Link>
    </section>
  );
}

/** Header glyph: readiness (outer) and sleep score (inner) as concentric rings → Health. */
function BodyGlyph({ health }: { health: TodayPayload['health'] }): JSX.Element | null {
  const { readiness, sleepScore } = health;
  if (readiness === undefined && sleepScore === undefined) return null;
  const label = [
    readiness !== undefined ? `Readiness ${Math.round(readiness)}` : null,
    sleepScore !== undefined ? `sleep score ${Math.round(sleepScore)}` : null,
  ]
    .filter(Boolean)
    .join(', ');
  return (
    <Link to="/health" className="home__glyph" aria-label={label} title={label}>
      <Ring value={readiness ?? 0} color="var(--accent)" size={40} stroke={5} label="">
        {sleepScore !== undefined && <Ring value={sleepScore} color="var(--ring-sleep)" size={26} stroke={5} label="" />}
      </Ring>
    </Link>
  );
}
